import { FC } from "react";
import { useResolveUploadedPath, useTrans } from "../../../hooks";
import { Text, HStack, Avatar, Badge, VStack } from "@chakra-ui/react";
import {
    DataType,
    IKaTableProps,
    ITableInstance,
    Table as KaTable,
    SortingMode,
} from "ka-table";
import { Column, PagingOptions } from "ka-table/models";
import { ICellTextProps } from "ka-table/props";
import { KaPageSizeSelector } from "../../../components";
import { APPC } from "../../../config";

export const StudentList: FC<{
    table: ITableInstance;
    paging: PagingOptions;
    list: any[];
    loadingList?: boolean;
    onActionClick?: (key: string, data?: any) => void;
    SelectionCell: FC<ICellTextProps>;
    SelectionHeader: FC;
}> = ({
    table,
    paging,
    list,
    loadingList = false,
    SelectionCell,
    SelectionHeader,
}) => {
    const { t } = useTrans();
    const columns: Column[] = [
        {
            key: "selection-cell",
            isFilterable: false,
            width: 50,
        },
        {
            key: "firstName",
            title: t("ent.User:name.label"),
            dataType: DataType.String,
        },
        {
            key: "phone",
            title: t("ent.User:phone.label"),
            dataType: DataType.String,
            isSortable: false,
        },
        {
            key: "schoolBatch?.name",
            title: t("ent.SchoolBatch:name.label"),
            dataType: DataType.String,
            isSortable: false,
        },
        {
            key: "status",
            title: t("ent.User:status.label"),
            dataType: DataType.String,
        },
        {
            key: "isActive",
            title: t("ent.User:isActive.label"),
            dataType: DataType.Boolean,
        },
    ];

    const kaTableProps: IKaTableProps = {
        table: table,
        columns: columns,
        data: list || [],
        loading: {
            enabled: loadingList,
        },
        paging: paging,
        rowKeyField: "id",
        sortingMode: SortingMode.SingleRemote,
        childComponents: {
            pagingSizes: {
                content: (props) => <KaPageSizeSelector {...props} />,
            },
            cellText: {
                content: (props: ICellTextProps) => {
                    switch (props?.column?.key) {
                        case "firstName":
                            return (
                                <HStack>
                                    <Avatar
                                        size={"sm"}
                                        name={props?.rowData?.fullName}
                                        src={useResolveUploadedPath(
                                            APPC.BE.Upload.FILETYPE
                                                .FILETYPE_USER_AVATAR,
                                            props?.rowData?.imageName
                                        )}
                                        color={
                                            "var(--chakra-colors-primaryS10)"
                                        }
                                        bgColor={
                                            "var(--chakra-colors-primaryT80)"
                                        }
                                    />
                                    <VStack gap={1} alignItems={"flex-start"}>
                                        <Text
                                            textStyle={"mediumSm"}
                                            color={"var(--chakra-colors-black)"}
                                            lineHeight={1}
                                        >
                                            {props?.rowData?.fullName}
                                        </Text>
                                        <Text
                                            textStyle={"regularXs"}
                                            lineHeight={1}
                                            color={
                                                "var(--chakra-colors-contentT40)"
                                            }
                                        >
                                            {props?.rowData?.email}
                                        </Text>
                                    </VStack>
                                </HStack>
                            );
                        case "status":
                            return (
                                <Badge
                                    variant={
                                        props?.rowData?.status ===
                                        APPC.BE.User.STUDENT_STATUS
                                            .STUDENT_STATUS_CONFIRMED
                                            ? "green"
                                            : "red"
                                    }
                                >
                                    {t(
                                        `con.User:STUDENT_STATUS_${props?.rowData?.status}`
                                    )}
                                </Badge>
                            );
                        case "schoolBatch?.name":
                            return (
                                <Text>{props?.rowData?.schoolBatch?.name}</Text>
                            );
                        case "isActive":
                            return (
                                <Badge
                                    variant={
                                        props?.rowData?.isActive
                                            ? "green"
                                            : "disabled"
                                    }
                                >
                                    {t(
                                        `cmn:label.active.${props?.rowData?.isActive}`
                                    )}
                                </Badge>
                            );
                        case "selection-cell":
                            return <SelectionCell {...props} />;
                    }
                },
            },
            headCell: {
                content: (props) => {
                    if (props.column.key === "selection-cell") {
                        return <SelectionHeader />;
                    }
                },
            },
        },
    };

    return <KaTable {...kaTableProps} />;
};
